'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface FormProgressContextType {
  currentStep: number;
  totalSteps: number;
  percentage: number;
  headline: string;
  showBack: boolean;
  onBack?: () => void;
  answers: Record<string, string>;
  draftAnswers: Record<string, string>;
  updateProgress: (step: number, total: number, percentage: number) => void;
  updateHeadline: (headline: string) => void;
  setBackHandler: (handler: (() => void) | undefined, show: boolean) => void;
  updateAnswers: (answers: Record<string, string>) => void;
  updateDraftAnswer: (key: string, value: string) => void;
}

const FormProgressContext = createContext<FormProgressContextType | undefined>(undefined);

export const FormProgressProvider = ({ children }: { children: ReactNode }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(10);
  const [percentage, setPercentage] = useState(0);
  const [headline, setHeadline] = useState('');
  const [showBack, setShowBack] = useState(false);
  const [onBack, setOnBack] = useState<(() => void) | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [draftAnswers, setDraftAnswers] = useState<Record<string, string>>({});

  const updateProgress = (step: number, total: number, percentage: number) => {
    setCurrentStep(step);
    setTotalSteps(total);
    setPercentage(percentage);
  };

  const updateHeadline = (newHeadline: string) => {
    setHeadline(newHeadline);
  };

  const setBackHandler = (handler: (() => void) | undefined, show: boolean) => {
    setOnBack(() => handler);
    setShowBack(show);
  };

  const updateAnswers = (next: Record<string, string>) => {
    setAnswers(next);
  };

  const updateDraftAnswer = (key: string, value: string) => {
    setDraftAnswers(prev => (prev[key] === value ? prev : { ...prev, [key]: value }));
  };

  return (
    <FormProgressContext.Provider value={{ currentStep, totalSteps, percentage, headline, showBack, onBack, answers, draftAnswers, updateProgress, updateHeadline, setBackHandler, updateAnswers, updateDraftAnswer }}>
      {children}
    </FormProgressContext.Provider>
  );
};

export const useFormProgress = () => {
  const context = useContext(FormProgressContext);
  if (context === undefined) {
    throw new Error('useFormProgress must be used within a FormProgressProvider');
  }
  return context;
};

